import React, { useState } from 'react';
import { Collapse, styled } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Colors } from 'library';
import { BaseButton } from './BaseButton';
import { Card, CardTone } from './Card';

type CardPadding = 'sm' | 'md' | 'lg';
type HeaderAlign = 'start' | 'center';

interface ExpandableCardProps {
    header: React.ReactNode;
    children: React.ReactNode;
    defaultExpanded?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    headerAlign?: HeaderAlign;
    padding?: CardPadding;
    tone?: CardTone;
}

export function ExpandableCard({
    header,
    children,
    defaultExpanded = false,
    expanded: controlledExpanded,
    onExpandedChange,
    headerAlign = 'center',
    padding = 'md',
    tone = 'default',
}: ExpandableCardProps) {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const isControlled = controlledExpanded !== undefined;
    const expanded = isControlled ? controlledExpanded : internalExpanded;

    const toggle = () => {
        const next = !expanded;
        if (!isControlled) {
            setInternalExpanded(next);
        }
        onExpandedChange?.(next);
    };

    return (
        <HoverCard padding={padding} tone={tone}>
            <HeaderButton type="button" onClick={toggle} $align={headerAlign}>
                <HeaderContent>{header}</HeaderContent>
                <Chevron $expanded={expanded}>
                    <ExpandMore sx={{ fontSize: 20, color: Colors.textSecondary }} />
                </Chevron>
            </HeaderButton>
            <Collapse in={expanded} timeout={250} unmountOnExit>
                <Body>{children}</Body>
            </Collapse>
        </HoverCard>
    );
}

const HoverCard = styled(Card)({
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        borderColor: Colors.secondary,
    },
});

const HeaderButton = styled(BaseButton, { shouldForwardProp: (prop) => prop !== '$align' })<{ $align: HeaderAlign }>(
    ({ $align }) => ({
        display: 'flex',
        flexDirection: 'row',
        alignItems: $align === 'start' ? 'flex-start' : 'center',
        gap: 16,
        width: '100%',
        textAlign: 'left',
    }),
);

const HeaderContent = styled('div')({
    flex: 1,
    minWidth: 0,
});

const Chevron = styled('span', { shouldForwardProp: (prop) => prop !== '$expanded' })<{ $expanded: boolean }>(
    ({ $expanded }) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transform: $expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
        transition: 'transform 200ms ease',
    }),
);

const Body = styled('div')({
    paddingTop: 16,
});
